import React, { useState } from 'react';
import './Register.css';

interface SiteRegistrationProps {
  onSuccess: () => void;
  onBack: () => void;
}

const SiteRegistration: React.FC<SiteRegistrationProps> = ({ onSuccess, onBack }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Account
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',

    // Site Information
    siteName: '',
    city: '',
    province: '',
    address: '',
    latitude: '',
    longitude: '',

    // Experience
    yearsOperating: '',
    previousTrials: '',
    therapeuticAreas: '',

    // Staffing
    principalInvestigator: '',
    coordinators: '',
    nurses: '',
    totalStaff: '',

    // Capacity
    patientPopulation: '',
    enrollmentCapacity: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    setError('');
    if (step === 1) {
      if (!formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
        setError('Please fill in all required fields');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
    }

    if (step === 2) {
      if (!formData.siteName || !formData.city || !formData.province || !formData.address) {
        setError('Please fill in all required site information');
        return;
      }
    }

    if (step === 3) {
      if (!formData.yearsOperating || !formData.previousTrials || !formData.therapeuticAreas) {
        setError('Please fill in all required experience fields');
        return;
      }
    }

    setStep(step + 1);
  };

  const handleBack = () => {
    setError('');
    setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (
        !formData.principalInvestigator ||
        !formData.coordinators ||
        !formData.nurses ||
        !formData.totalStaff
      ) {
        setError('Please fill in all staffing information');
        setLoading(false);
        return;
      }

      if (!formData.patientPopulation || !formData.enrollmentCapacity) {
        setError('Please fill in capacity information');
        setLoading(false);
        return;
      }

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'site',
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          profile: {
            type: 'site',
            siteName: formData.siteName,
            location: {
              city: formData.city,
              province: formData.province,
              address: formData.address,
              coordinates: {
                lat: parseFloat(formData.latitude) || 0,
                lng: parseFloat(formData.longitude) || 0,
              },
            },
            experience: {
              yearsOperating: parseInt(formData.yearsOperating),
              previousTrials: parseInt(formData.previousTrials),
              therapeuticAreas: formData.therapeuticAreas.split(',').map((s) => s.trim()),
            },
            staffing: {
              pi: formData.principalInvestigator,
              coordinators: parseInt(formData.coordinators),
              nurses: parseInt(formData.nurses),
              totalStaff: parseInt(formData.totalStaff),
            },
            capacity: {
              patientPopulation: parseInt(formData.patientPopulation),
              enrollmentCapacity: parseInt(formData.enrollmentCapacity),
            },
            feasibilityScore: 0,
            isPublic: true,
          },
        }),
      });

      const data = await response.json();

      if (data.ok || response.ok) {
        alert(
          'Registration successful! Your clinical trial site has been registered. You can now login with your credentials.'
        );
        onSuccess();
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container large">
        <div className="register-header">
          <button onClick={onBack} className="back-button">
            ← Back
          </button>
          <h1>Clinical Trial Site Registration</h1>
          <p>Step {step} of 4</p>
          <div className="free-badge-large">FREE Membership</div>
        </div>

        <div className="progress-bar">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>Account</div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>Site Info</div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>Experience</div>
          <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>Staffing</div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="registration-form">
          {step === 1 && (
            <div className="form-step">
              <h2>Account Information</h2>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password *</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                />
                <small>At least 8 characters</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>

              <button type="button" onClick={handleNext} className="next-button">
                Next →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <h2>Site Information</h2>

              <div className="form-group">
                <label htmlFor="siteName">Site Name *</label>
                <input
                  id="siteName"
                  name="siteName"
                  type="text"
                  value={formData.siteName}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="province">Province *</label>
                <select
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Province</option>
                  <option value="Western Cape">Western Cape</option>
                  <option value="Gauteng">Gauteng</option>
                  <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                  <option value="Eastern Cape">Eastern Cape</option>
                  <option value="Limpopo">Limpopo</option>
                  <option value="Mpumalanga">Mpumalanga</option>
                  <option value="North West">North West</option>
                  <option value="Free State">Free State</option>
                  <option value="Northern Cape">Northern Cape</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="city">City *</label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Physical Address *</label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="latitude">Latitude (Optional)</label>
                  <input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="0.0001"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="-26.2041"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="longitude">Longitude (Optional)</label>
                  <input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="0.0001"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="28.0473"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleBack} className="back-button-form">
                  ← Back
                </button>
                <button type="button" onClick={handleNext} className="next-button">
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <h2>Experience & Expertise</h2>

              <div className="form-group">
                <label htmlFor="yearsOperating">Years Operating *</label>
                <input
                  id="yearsOperating"
                  name="yearsOperating"
                  type="number"
                  value={formData.yearsOperating}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="previousTrials">Number of Previous Trials *</label>
                <input
                  id="previousTrials"
                  name="previousTrials"
                  type="number"
                  value={formData.previousTrials}
                  onChange={handleChange}
                  required
                  min="0"
                />
              </div>

              <div className="form-group">
                <label htmlFor="therapeuticAreas">Therapeutic Areas of Expertise *</label>
                <input
                  id="therapeuticAreas"
                  name="therapeuticAreas"
                  type="text"
                  value={formData.therapeuticAreas}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Oncology, Cardiology, Diabetes"
                />
                <small>Separate multiple areas with commas</small>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleBack} className="back-button-form">
                  ← Back
                </button>
                <button type="button" onClick={handleNext} className="next-button">
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="form-step">
              <h2>Staffing & Capacity</h2>

              <div className="form-group">
                <label htmlFor="principalInvestigator">Principal Investigator *</label>
                <input
                  id="principalInvestigator"
                  name="principalInvestigator"
                  type="text"
                  value={formData.principalInvestigator}
                  onChange={handleChange}
                  required
                  placeholder="Dr. John Smith"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="coordinators">Number of Coordinators *</label>
                  <input
                    id="coordinators"
                    name="coordinators"
                    type="number"
                    value={formData.coordinators}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="nurses">Number of Nurses *</label>
                  <input
                    id="nurses"
                    name="nurses"
                    type="number"
                    value={formData.nurses}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="totalStaff">Total Staff Members *</label>
                <input
                  id="totalStaff"
                  name="totalStaff"
                  type="number"
                  value={formData.totalStaff}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="patientPopulation">Patient Population *</label>
                  <input
                    id="patientPopulation"
                    name="patientPopulation"
                    type="number"
                    value={formData.patientPopulation}
                    onChange={handleChange}
                    required
                    min="0"
                  />
                  <small>Catchment area population</small>
                </div>
                <div className="form-group">
                  <label htmlFor="enrollmentCapacity">Enrollment Capacity *</label>
                  <input
                    id="enrollmentCapacity"
                    name="enrollmentCapacity"
                    type="number"
                    value={formData.enrollmentCapacity}
                    onChange={handleChange}
                    required
                    min="1"
                  />
                  <small>Participants per trial</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={handleBack} className="back-button-form">
                  ← Back
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Registering...' : 'Complete Registration'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SiteRegistration;
